<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { DataObjectDeleteDocument, DataObjectType } from '@/gql/graphql';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { formatFileName } from '@/utils/file-utils';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  dataObject: DataObjectType;
  bucketId: number;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});

const objectType = computed<string>(() => {
  if (props.dataObject) {
    return props.dataObject.isFolder ? 'Folder' : 'File';
  }
  return '';
});

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(DataObjectDeleteDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.dataObjectDelete) {
    console.debug('Data object deleted:', res.data.dataObjectDelete);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Deletion failed: ' + res.data;
    console.error('Data object deletion failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error deleting data object:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onDeletionConfirmed() {
  if (!props.dataObject) {
    mutationError.value = 'Data object not provided for deletion';
    console.error('Data object not provided for deletion');
    return;
  }
  if (typeof props.dataObject.name !== 'string') {
    mutationError.value = 'Data object name is not a string';
    console.error('Data object name is not a string:', props.dataObject.name);
    return;
  }
  if (typeof props.dataObject.isFolder !== 'boolean') {
    mutationError.value = 'Data object `isFolder` field type is not a boolean';
    console.error(
      'Data object `isFolder` field type is not a boolean:',
      props.dataObject.isFolder
    );
    return;
  }

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    bucketId: props.bucketId,
    objectName: props.dataObject.name,
    isFolder: props.dataObject.isFolder,
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL: Storage Object Delete -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="trash" class="me-2" />
        Delete
        <strong>{{ formatFileName(dataObject?.name) }}</strong>
        {{ objectType.toLowerCase() }}
      </template>
      <!-- Content -->
      <div v-if="dataObject">
        <!-- Delete? Confirmation Message -->
        <p>
          Are you sure you want to delete the {{ objectType.toLowerCase() }}
          <strong> {{ formatFileName(dataObject.name) }} </strong>?
        </p>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="mutationInProgress || mutationShowSuccess"
          @click="onDeletionConfirmed"
        >
          <span v-if="mutationInProgress">
            Deleting {{ objectType }}
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            {{ objectType }} Deleted
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Delete {{ objectType }} </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
